/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { userService } from '../services/userService';
import toastService from '../utils/toastService';
import isEmail from 'validator/lib/isEmail';
import { X, PlusCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';

const NewUser = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [roles, setRoles] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [subAccounts, setSubAccounts] = useState([]);

  const [form, setForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    displayName: '',
    password: '',
    status: 'active',
    roleKeys: [],
    accountUID: '',
    subAccountUID: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});

  const isPlatformAdmin = currentUser?.scope === 'platform';
  const isAccountAdmin = currentUser?.scope === 'account';

  const currentScope = currentUser?.scope || 'platform';

  // Filter roles based on current context
  const filteredRoles = useMemo(() => {
    // Filter out platform_admin role unless the current user is a platform_admin
    const isPlatformAdminRole = currentUser.role === 'platform_admin';

    let filtered = roles;

    // Hide platform_admin role from everyone except platform_admin users
    if (!isPlatformAdminRole) {
      filtered = filtered.filter(r => r.id !== 'PLATFORM_ADMIN' && r.key !== 'Platform Admin');
    }

    // Platform admin with no context: show all roles
    if (isPlatformAdmin && !currentUser?.accountUID && !currentUser?.subAccountUID) {
      return filtered;
    }

    // Platform admin in account context: show account and sub-account roles
    if (isPlatformAdmin && currentUser?.accountUID && !currentUser?.subAccountUID) {
      return filtered.filter(r => r.rawScope === 'account' || r.rawScope === 'sub_account');
    }

    // Platform admin in sub-account context: show only sub-account roles
    if (isPlatformAdmin && currentUser?.subAccountUID) {
      return filtered.filter(r => r.rawScope === 'sub_account');
    }

    // Account admin: show account and sub-account roles
    if (isAccountAdmin) {
      return filtered.filter(r => r.rawScope === 'account' || r.rawScope === 'sub_account');
    }

    // Sub-account admin: show only sub-account roles
    if (currentScope === 'sub_account') {
      return filtered.filter(r => r.rawScope === 'sub_account');
    }

    return filtered;
  }, [roles, currentScope, isPlatformAdmin, isAccountAdmin, currentUser?.accountUID, currentUser?.subAccountUID]);

  // Derive the scope required by selected roles
  const requiredScope = useMemo(() => {
    if (!form.roleKeys.length) { return null; }
    const selected = filteredRoles.filter(r => form.roleKeys.includes(r.id));
    if (selected.some(r => r.rawScope === 'sub_account')) { return 'sub_account'; }
    if (selected.some(r => r.rawScope === 'account')) { return 'account'; }
    return 'platform';
  }, [form.roleKeys, filteredRoles]);

  // Determine if dropdowns should be shown based on context and role requirements
  const needsAccount = isPlatformAdmin && !currentUser?.accountUID && (requiredScope === 'account' || requiredScope === 'sub_account');

  const needsSubAccount = (() => {
    // Platform admin in platform context: show dropdown for sub-account roles
    if (isPlatformAdmin && !currentUser?.accountUID && !currentUser?.subAccountUID) {
      return requiredScope === 'sub_account';
    }
    // Platform admin with account context: show dropdown for sub-account roles
    if (isPlatformAdmin && currentUser?.accountUID && !currentUser?.subAccountUID) {
      return requiredScope === 'sub_account';
    }
    // Platform admin with sub-account context: never show sub-account dropdown
    if (isPlatformAdmin && currentUser?.subAccountUID) {
      return false;
    }
    // Account admin: show dropdown for sub-account roles
    if (isAccountAdmin) {
      return requiredScope === 'sub_account';
    }
    return false;
  })();
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const scopeLabelMap = {
          account: 'Account',
          platform: 'Platform',
          sub_account: 'Sub Account',
        };
        const res = await userService.getRoles();
        const rolesData = res.data?.roles || res.data?.data || (Array.isArray(res.data) ? res.data : []);
        setRoles(rolesData.map(r => ({
          id: r.key || r.name?.toUpperCase() || r.id,
          key: r.name,
          module: r.scope === 'sub_account' ? 'Sub-Account' : scopeLabelMap[r.scope],
          rawScope: r.scope,
          description: r.description,
        })));
      } catch {
        toastService.error('You are not authorized to view roles.');
      }
    };
    fetchRoles();
  }, []);

  // Load accounts for platform admin when account picker appears
  useEffect(() => {
    if (!needsAccount || accounts.length) { return; }
    apiClient.get('/accounts', { params: { limit: 100, page: 1, status: 'active' } })
      .then(({ data }) => { setAccounts(data.data || []) })
      .catch(() => toastService.error('Failed to load accounts'));
  }, [needsAccount]);

  // Load sub-accounts when account is selected or from context
  useEffect(() => {
    if (!needsSubAccount) { setSubAccounts([]); return; }

    let accountId;
    if (isPlatformAdmin && !currentUser?.accountUID) {
      // Platform admin in platform context: use selected account
      accountId = form.accountUID;
    } else if (isPlatformAdmin && currentUser?.accountUID) {
      // Platform admin with account context: use context account
      accountId = currentUser.accountUID;
    } else if (isAccountAdmin) {
      // Account admin: use their account
      accountId = currentUser?.accountUID;
    } else{
      // Sub-account admin: use their account
    }

    if (!accountId) { setSubAccounts([]); return; }
    apiClient.get(`/accounts/${accountId}/sub-accounts`, { params: { limit: 100, page: 1, status: 'active' } })
      .then(({ data }) => setSubAccounts(data.subAccounts || data.data || []))
      .catch(() => toastService.error('Failed to load sub-accounts'));
  }, [needsSubAccount, form.accountUID, currentUser?.accountUID, isPlatformAdmin, isAccountAdmin]);

  // Reset account/sub-account when scope changes
  useEffect(() => {
    setForm(f => ({ ...f, accountUID: '', subAccountUID: '' }));
    setSubAccounts([]);
  }, [requiredScope]);


  const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])\S{8,128}$/;

  const validatePassword = (password) => {
    if (!password?.trim()) {
      return null; // Optional for Edit User
    }

    return PASSWORD_REGEX.test(password)
      ? null
      : 'Password must be between 8 and 128 characters long and include at least one uppercase letter, one lowercase letter, one number, one special character, and no spaces.';
  };

  const validateForm = () => {
    const errors = {};
    if (!form.email) { errors.email = 'Email is required'; }
    else if (!isEmail(form.email)) { errors.email = 'Invalid email format'; }
    else{ 
      // No error
     }
    if (!form.firstName) { errors.firstName = 'First name is required'; }
    if (!form.lastName) { errors.lastName = 'Last name is required'; }
    if (!form.displayName) { errors.displayName = 'Display name is required'; }
    const passwordError = validatePassword(form.password);
    if (passwordError) { errors.password = passwordError; }
    if (!form.roleKeys.length) { errors.roleKeys = 'At least one role is required'; }

    // Only validate account selection if dropdown is shown
    if (needsAccount && !form.accountUID && !currentUser?.accountUID) {
      errors.accountUID = 'Account is required for this role';
    }

    // Only validate sub-account selection if dropdown is shown
    if (needsSubAccount && !form.subAccountUID && !currentUser?.subAccountUID) {
      errors.subAccountUID = 'Sub-account is required for this role';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBlur = (field, value) => {
    if (!value || !String(value).trim()) {
      const labels = { firstName: 'First name', lastName: 'Last name', displayName: 'Display name', email: 'Email', password: 'Password', roleKeys: 'Role' };
      setFieldErrors(prev => ({ ...prev, [field]: `${labels[field] || field} is required` }));
    } else if (field === 'email' && !isEmail(value)) {
      setFieldErrors(prev => ({ ...prev, email: 'Invalid email format' }));
    } else if (field === 'password') {
      const error = validatePassword(value);
      if (error) setFieldErrors(prev => ({ ...prev, password: error }));
    }else{
      setFieldErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { return; }
    try {
      setSubmitting(true);
      const payload = {
        email: form.email,
        password: form.password,
        firstName: form.firstName,
        lastName: form.lastName,
        displayName: form.displayName,
        roleKeys: form.roleKeys,
        status: form.status,
      };

      // Add accountUID based on context and role requirements
      if (requiredScope === 'account' || requiredScope === 'sub_account') {
        if (isPlatformAdmin && !currentUser?.accountUID) {
          // Platform admin in platform context: use selected account
          payload.accountUID = form.accountUID;
        } else if (isPlatformAdmin && currentUser?.accountUID) {
          // Platform admin with account context: use context account
          payload.accountUID = currentUser.accountUID;
        } else if (isAccountAdmin) {
          // Account admin: use their account
          payload.accountUID = currentUser?.accountUID;
        } else if (currentScope === 'sub_account') {
          // Sub-account admin: use their account
          payload.accountUID = currentUser?.accountUID;
        } else{
          payload.accountUID = null;
        }
      }

      // Add subAccountUID based on context and role requirements
      if (requiredScope === 'sub_account') {
        if (isPlatformAdmin && currentUser?.subAccountUID) {
          // Platform admin with sub-account context: use context sub-account
          payload.subAccountUID = currentUser.subAccountUID;
        } else if (currentScope === 'sub_account' && currentUser?.subAccountUID) {
          // Sub-account admin: use their sub-account
          payload.subAccountUID = currentUser.subAccountUID;
        } else if (form.subAccountUID) {
          // Otherwise use selected sub-account
          payload.subAccountUID = form.subAccountUID;
        }else{
          payload.subAccountUID = null;
        }
      }

      await userService.createUser(payload);
      toastService.success('User created successfully');
      navigate('/users');
    } catch (err) {
      const msg = err.response?.data?.message;
      toastService.error(Array.isArray(msg) ? msg[0] : (msg || 'Failed to create user'));
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-4 py-3 bg-base-100 border rounded-xl text-base-content placeholder:text-base-content/20 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all ${fieldErrors[field] ? 'border-error' : 'border-base-300'}`;

  const selectClass = (field) =>
    `w-full px-4 py-3 bg-base-100 border rounded-xl text-base-content focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all ${fieldErrors[field] ? 'border-error' : 'border-base-300'}`;

  return (
    <div className="flex flex-col h-auto lg:h-[calc(100vh-3rem)] lg:overflow-hidden rounded-2xl border border-base-300 shadow-sm bg-base-100">
      <div className="flex-1 flex flex-col overflow-y-auto bg-base-100/50 backdrop-blur-md">

        <div className="p-6 sm:p-8 border-b border-base-300 flex-shrink-0 flex justify-between items-start">
          <div>
            <h1 className="page-title mb-1 flex items-baseline gap-2">
              <span className="shrink-0">Create New User</span>
            </h1>
          </div>
          <button onClick={() => navigate('/users')} className="p-2 hover:bg-base-200 rounded-xl transition-colors text-base-content/40 hover:text-base-content" title="Return to users list">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <label htmlFor="firstName" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-2 block">First Name *</label>
                <input id="firstName" type="text" maxLength={30} placeholder="e.g. John" className={inputClass('firstName')} value={form.firstName}
                  onBlur={e => handleBlur('firstName', e.target.value)}
                  onChange={e => { setForm(f => ({ ...f, firstName: e.target.value })); setFieldErrors(p => ({ ...p, firstName: null })); }} />
                {fieldErrors.firstName && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.firstName}</p>}
              </div>
              <div className="space-y-2">
                <label htmlFor="lastName" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-2 block">Last Name *</label>
                <input id="lastName" type="text" maxLength={30} placeholder="e.g. Doe" className={inputClass('lastName')} value={form.lastName}
                  onBlur={e => handleBlur('lastName', e.target.value)}
                  onChange={e => { setForm(f => ({ ...f, lastName: e.target.value })); setFieldErrors(p => ({ ...p, lastName: null })); }} />
                {fieldErrors.lastName && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.lastName}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <label htmlFor="email" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-2 block">Email Address *</label>
                <input id="email" type="email" maxLength={100} placeholder="john.doe@company.com" className={inputClass('email')} value={form.email}
                  onBlur={e => handleBlur('email', e.target.value)}
                  onChange={e => { setForm(f => ({ ...f, email: e.target.value })); setFieldErrors(p => ({ ...p, email: null })); }} />
                {fieldErrors.email && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.email}</p>}
              </div>
              <div className="space-y-2">
                <label htmlFor="password" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-2 block">Password *</label>
                <input id="password" type="password" autoComplete="new-password" name="new-password" placeholder="••••••••" className={inputClass('password')} value={form.password}
                  onBlur={e => handleBlur('password', e.target.value)}
                  onChange={e => { setForm(f => ({ ...f, password: e.target.value })); setFieldErrors(p => ({ ...p, password: null })); }} />
                {fieldErrors.password && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.password}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <label htmlFor="displayName" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-2 block">Display Name *</label>
                <input id="displayName" type="text" maxLength={30} placeholder="e.g. John D." className={inputClass('displayName')} value={form.displayName}
                  onBlur={e => handleBlur('displayName', e.target.value)}
                  onChange={e => { setForm(f => ({ ...f, displayName: e.target.value })); setFieldErrors(p => ({ ...p, displayName: null })); }} />
                {fieldErrors.displayName && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.displayName}</p>}
              </div>
              <div className="space-y-2">
                <label htmlFor="status" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">Status</label>
                <div className="flex items-center gap-4 p-3 rounded-xl border border-base-300 h-[50px]">
                  <label aria-label="User Status Toggle" className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" id="status" className="sr-only peer" checked={form.status === 'active'}
                      onChange={e => setForm(f => ({ ...f, status: e.target.checked ? 'active' : 'inactive' }))} />
                    <div className="w-10 h-5 bg-base-content/10 rounded-full peer peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
                  </label>
                  <label htmlFor="status" className="text-[10px] font-black text-base-content/60 uppercase tracking-[0.2em] cursor-pointer whitespace-nowrap">
                    {form.status === 'active' ? 'Active' : 'Inactive'}
                  </label>
                </div>
              </div>
            </div>

            {/* Role Picker */}
            <div className="space-y-2">
              <label htmlFor="role" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-2 block">Role *</label>
              <select
                id="role"
                className={selectClass('roleKeys')}
                value={form.roleKeys[0] || ''}
                onChange={e => { setForm(f => ({ ...f, roleKeys: e.target.value ? [e.target.value] : [] })); setFieldErrors(p => ({ ...p, roleKeys: null })); }}
              >
                <option value="">— Select a role —</option>
                {filteredRoles.sort((a, b) => a.key.localeCompare(b.key, undefined, { sensitivity: 'base' })).map(role => (
                  <option key={role.id} value={role.id}>
                    {role.key} ({role.module})
                  </option>
                ))}
              </select>
              {fieldErrors.roleKeys && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.roleKeys}</p>}
            </div>

            {/* Account picker — only for platform admin when account/sub-account role selected */}
            {needsAccount && (
              <div className="space-y-2">
                <label htmlFor="account" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-2 block">Account *</label>
                <select id="account" className={selectClass('accountUID')} value={form.accountUID}
                  onChange={e => { setForm(f => ({ ...f, accountUID: e.target.value, subAccountUID: '' })); setFieldErrors(p => ({ ...p, accountUID: null })); }}>
                  <option value="">— Select an account —</option>
                  {accounts.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })).map(a => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
                {fieldErrors.accountUID && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.accountUID}</p>}
              </div>
            )}

            {/* Sub-account picker — when sub_account scope is required */}
            {needsSubAccount && (
              <div className="space-y-2">
                <label htmlFor="subAccount" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-2 block">Sub-Account *</label>
                <select id="subAccount" className={selectClass('subAccountUID')} value={form.subAccountUID}
                  onChange={e => { setForm(f => ({ ...f, subAccountUID: e.target.value })); setFieldErrors(p => ({ ...p, subAccountUID: null })); }}
                  disabled={isPlatformAdmin && !currentUser?.accountUID && !form.accountUID}>
                  <option value="">{isPlatformAdmin && !currentUser?.accountUID && !form.accountUID ? '— Select an account first —' : '— Select a sub-account —'}</option>
                  {subAccounts.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })).map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                {fieldErrors.subAccountUID && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.subAccountUID}</p>}
              </div>
            )}

            <div className="flex gap-4 pt-12 border-t border-base-content/5">
              <button type="button" onClick={() => navigate('/users')}
                className="flex-1 py-4 px-8 border border-base-content/20 rounded-xl font-black uppercase tracking-[0.2em] text-xs text-base-content/40 hover:bg-base-content/5 hover:text-base-content transition-all">
                Cancel
              </button>
              <button type="submit" disabled={submitting} className="flex-1 btn-premium">
                {submitting ? (
                  <><div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>Committing...</>
                ) : (
                  <><PlusCircle size={18} />Create User</>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewUser;
